import React, { useState, useRef } from 'react';
import { Layout, Menu, Button, Dropdown, Badge, Space, Typography, Drawer, Modal, Popover, Avatar } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  CalendarOutlined,
  FileTextOutlined,
  FolderOutlined,
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  ThunderboltOutlined,
  WifiOutlined,
  ReloadOutlined,
  CheckOutlined,
  CustomerServiceOutlined,
  GlobalOutlined,
  CalculatorOutlined,
  CheckSquareOutlined,
  BugOutlined,
  CheckCircleOutlined,
  MenuOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { useCognitoAuth } from '../../contexts/CognitoAuthContext';
import { useApp, BusinessArea } from '../../contexts/AppContext';
import { getUserIdAsNumber, getUserRole } from '../../utils/cognitoUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../services/api';
import { isAdmin, canSwitchBusinessArea, canAccessSimuladores, canAccessTeamsScreen, isGestorEquipaOrAbove } from '../../utils/roleUtils';
import DEPLOYMENT_CONFIG from '../../config/deployment';
import ReportModal from '../ReportModal';
import VideoBanner from '../VideoBanner';
import { USE_SEASONAL_ICONS, SEASONAL_BELL_ICON_PATH, SEASONAL_BUG_ICON_PATH, ORIGINAL_BELL_ICON, ORIGINAL_BUG_ICON } from '../../constants/iconConstants';
import './MainLayout.css';
// Chat functionality disabled
// import FloatingChatButton from '../FloatingChatButton';
// import ChatModal from '../ChatModal';

const { Header, Content } = Layout;
const { Text } = Typography;
const TOP_BANNER_ASSET_VERSION = '2026-05-06-1';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { user, logout } = useCognitoAuth();
  const { 
    currentBusinessArea, 
    setCurrentBusinessArea, 
    notifications,
    fetchNotifications,
    loading
  } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const [notificationModalVisible, setNotificationModalVisible] = useState(false);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  // Chat functionality disabled
  // const [chatModalVisible, setChatModalVisible] = useState(false);
  const notifBtnRef = useRef<HTMLButtonElement>(null);

  const canSwitchBusinessAreaFlag = user ? canSwitchBusinessArea(getUserRole(user)) : false;

  const handleNotificationOpen = (open: boolean) => {
    setNotifOpen(open);
    if (open) {
      // Refresh notifications when dropdown is opened
      fetchNotifications();
    }
  };

  const handleRefreshNotifications = () => {
    fetchNotifications();
  };

  const handleNotificationClick = (notification: any) => {
    setSelectedNotification(notification);
    setNotificationModalVisible(true);
    setNotifOpen(false);
    
    // Mark as read if not already read
    if (!notification.read) {
      handleMarkAsRead(notification.id);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await apiService.markNotificationAsRead(notificationId);
      // Refresh notifications to update the UI
      fetchNotifications();
    } catch (error) {
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead(getUserIdAsNumber(user));
      // Refresh notifications to update the UI
      fetchNotifications();
    } catch (error) {
    }
  };

  // Chat functionality disabled
  // const handleChatModalOpen = () => {
  //   setChatModalVisible(true);
  // };

  // const handleChatModalClose = () => {
  //   setChatModalVisible(false);
  // };

  const getMenuItems = () => {
    const userRole = user ? getUserRole(user) : '';

    // Non-admin users only see Página Principal and Workspace de Cliente
    if (!isAdmin(userRole)) {
      return [
        {
          key: '/',
          icon: <GlobalOutlined />,
          label: 'Página Principal',
        },
        {
          key: '/client-workspace',
          icon: <CustomerServiceOutlined />,
          label: 'Workspace de Cliente',
        },
      ];
    }

    const baseItems = [
      {
        key: '/',
        icon: <GlobalOutlined />,
        label: 'Página Principal',
      },
      {
        key: '/leads',
        icon: <FileTextOutlined />, 
        label: 'Leads',
      },
      {
        key: '/taskmaster',
        icon: <CheckSquareOutlined />, 
        label: 'Gestão de Tarefas',
      },
      {
        key: '/client-management',
        icon: <CustomerServiceOutlined />, 
        label: 'Gestão de Clientes',
      },
      {
        key: '/client-workspace',
        icon: <CustomerServiceOutlined />, 
        label: 'Workspace de Cliente',
      },
      {
        key: '/cobertura-fidelizacao',
        icon: <GlobalOutlined />, 
        label: 'Cobertura e Fidelização',
      },
    ];

    // Add Simuladores only if user has access
    if (user && user.role && user.business_area && canAccessSimuladores(user.role, user.business_area, currentBusinessArea)) {
      baseItems.push({
        key: '/simuladores',
        icon: <CalculatorOutlined />, 
        label: 'Simuladores',
      });
    }

    baseItems.push(
      {
        key: '/calendar',
        icon: <CalendarOutlined />, 
        label: 'Calendário',
      }
    );

    // Add Teams only if user has access (DC and Admin only)
    if (user && canAccessTeamsScreen(userRole)) {
      baseItems.push({
        key: '/teams',
        icon: <TeamOutlined />, 
        label: 'Equipas',
      });
    }

    // Add Team Validation & Processing for Gestor de Equipa and above
    if (user && isGestorEquipaOrAbove(userRole)) {
      baseItems.push({
        key: '/team-validation',
        icon: <CheckCircleOutlined />, 
        label: 'Validação de Equipa',
      });
    }

    baseItems.push({
      key: '/users',
      icon: <UserOutlined />, 
      label: 'Utilizadores',
    });
    baseItems.push({
      key: '/solutio',
      icon: <FolderOutlined />, 
      label: 'Solutio',
    });
    baseItems.push({
      key: '/sales-manager',
      icon: <ShoppingCartOutlined />, 
      label: 'Gestor de Vendas',
    });
    baseItems.push({
      key: '/files-processing',
      icon: <FolderOutlined />,
      label: 'Processamento de Ficheiros',
    });

    baseItems.push({
      key: '/settings',
      icon: <SettingOutlined />, 
      label: 'Definições',
    });

    return baseItems;
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleBusinessAreaChange = (area: BusinessArea) => {
    setCurrentBusinessArea(area);
    // Navigate to main page when switching areas
    navigate('/');
  };

  const userMenuItems = [
    ...(user && isAdmin(getUserRole(user))
      ? [{
          key: 'settings',
          icon: <SettingOutlined />,
          label: 'Definições',
        }]
      : []),
    ...(user && isAdmin(getUserRole(user))
      ? [{ type: 'divider' as const }]
      : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Terminar Sessão',
      onClick: logout,
    },
  ];

  const businessAreaItems = [
    {
      key: 'telecommunications',
      icon: <WifiOutlined />,
      label: 'Telecomunicações',
    },
    {
      key: 'energy_solutions',
      icon: <ThunderboltOutlined />,
      label: 'Energia',
    },
  ];

  const getBusinessAreaIcon = () => {
    return currentBusinessArea === 'telecommunications' ? <WifiOutlined /> : <ThunderboltOutlined />;
  };

  const getBusinessAreaLabel = () => {
    return currentBusinessArea === 'telecommunications' ? 'Telecomunicações' : 'Energia';
  };

  // Notification dropdown overlay
  const notificationOverlay = (
    <div
      style={{
        minWidth: 320,
        maxWidth: 400,
        maxHeight: 400,
        overflowY: 'auto',
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 4px 24px 0 rgba(24, 144, 255, 0.10)',
        padding: 0,
        marginTop: 8,
      }}
      className="crm-notification-dropdown"
    >
      <div style={{ 
        padding: '12px 20px', 
        borderBottom: '1px solid #f0f0f0', 
        fontWeight: 600, 
        fontSize: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>Notificações</span>
        <Space>
          <Button
            type="text"
            icon={<CheckOutlined />}
            size="small"
            onClick={handleMarkAllAsRead}
            style={{ border: 'none', padding: '4px 8px' }}
            title="Marcar todas como lidas"
          />
          <Button
            type="text"
            icon={<ReloadOutlined spin={loading} />}
            size="small"
            onClick={handleRefreshNotifications}
            style={{ border: 'none', padding: '4px 8px' }}
            title="Atualizar"
          />
        </Space>
      </div>
      <div style={{ minHeight: 60, padding: '12px 20px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#888', fontSize: 15, padding: '24px 0' }}>
            A carregar notificações...
          </div>
        ) : notifications.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#888', fontSize: 15, padding: '24px 0' }}>
            Sem notificações por agora
          </div>
        ) : (
          notifications.map((n, idx) => (
            <div 
              key={n.id || idx} 
              onClick={() => handleNotificationClick(n)}
              style={{
                padding: '10px 0',
                borderBottom: idx !== notifications.length - 1 ? '1px solid #f0f0f0' : 'none',
                color: n.read ? '#555' : '#1890ff',
                fontWeight: n.read ? 400 : 600,
                fontSize: 15,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span style={{ flex: 1 }}>{n.title || n.message}</span>
              {!n.read && <span style={{ width: 8, height: 8, borderRadius: 4, background: '#1890ff', display: 'inline-block' }} />}
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderNotificationDetails = (notification: any) => {
    const { notification_type, meta_data } = notification;
    
    switch (notification_type) {
      case 'event_creation':
        return (
          <div>
            <div><strong>Evento:</strong> {meta_data?.activity_title || 'N/A'}</div>
            <div><strong>Tipo:</strong> {meta_data?.activity_type || 'N/A'}</div>
            <div><strong>Data:</strong> {meta_data?.due_date ? new Date(meta_data.due_date).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' }) : 'N/A'}</div>
          </div>
        );
        
      case 'activity':
        return (
          <div>
            <div><strong>Evento:</strong> {meta_data?.activity_title || 'N/A'}</div>
            <div><strong>Tipo:</strong> {meta_data?.activity_type || 'N/A'}</div>
            <div><strong>Data:</strong> {meta_data?.due_date ? new Date(meta_data.due_date).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' }) : 'N/A'}</div>
            <div><strong>Importância:</strong> {meta_data?.importance_level === 3 ? 'Alta' : meta_data?.importance_level === 2 ? 'Média' : 'Baixa'}</div>
          </div>
        );
        
      case 'reminder':
        return (
          <div>
            <div><strong>Evento:</strong> {meta_data?.activity_title || 'N/A'}</div>
            <div><strong>Tipo de Lembrete:</strong> {meta_data?.reminder_type || 'N/A'}</div>
            <div><strong>Hora do Lembrete:</strong> {meta_data?.reminder_time ? new Date(meta_data.reminder_time).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' }) : 'N/A'}</div>
          </div>
        );
        
      case 'assignment':
        return (
          <div>
            <div><strong>Lead:</strong> {meta_data?.lead_name || 'N/A'}</div>
            <div><strong>Área de Negócio:</strong> {meta_data?.business_area || 'N/A'}</div>
            <div><strong>Tipo de Cliente:</strong> {meta_data?.customer_type || 'N/A'}</div>
            <div><strong>Atribuído em:</strong> {meta_data?.assignment_timestamp ? new Date(meta_data.assignment_timestamp).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' }) : 'N/A'}</div>
          </div>
        );
        
      case 'batch_assignment':
        return (
          <div>
            <div><strong>Número de Leads:</strong> {meta_data?.lead_count || 'N/A'}</div>
            <div><strong>Atribuído por:</strong> {meta_data?.assigned_by || 'N/A'}</div>
            <div><strong>Áreas de Negócio:</strong> {meta_data?.business_areas ? Object.entries(meta_data.business_areas).map(([area, count]) => `${area}: ${count}`).join(', ') : 'N/A'}</div>
            <div><strong>Atribuído em:</strong> {meta_data?.assignment_timestamp ? new Date(meta_data.assignment_timestamp).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' }) : 'N/A'}</div>
          </div>
        );
        
      case 'duplicate_lead':
        return (
          <div>
            <div><strong>DIF Duplicado:</strong> {meta_data?.duplicate_dif || 'N/A'}</div>
            <div><strong>Nome:</strong> {meta_data?.duplicate_nome || 'N/A'}</div>
            <div><strong>Lead Original ID:</strong> {meta_data?.existing_lead_id || 'N/A'}</div>
            <div><strong>Detetado em:</strong> {meta_data?.import_timestamp ? new Date(meta_data.import_timestamp).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' }) : 'N/A'}</div>
          </div>
        );
        
      case 'info':
      case 'success':
        // Handle task notifications
        if (notification.task_id) {
          return (
            <div>
              <div><strong>Tarefa:</strong> {notification.title}</div>
              <div><strong>Mensagem:</strong> {notification.message}</div>
              <div><strong>Status:</strong> {notification.notification_type === 'success' ? 'Concluída' : 'Atribuída'}</div>
              <div><strong>Criada em:</strong> {new Date(notification.created_at).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' })}</div>
            </div>
          );
        }
        return (
          <div>
            <div><strong>Mensagem:</strong> {notification.message}</div>
            <div><strong>Tipo:</strong> {notification.notification_type}</div>
            <div><strong>Criada em:</strong> {new Date(notification.created_at).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' })}</div>
          </div>
        );
        
      default:
        return (
          <div>
            <div><strong>Tipo de Notificação:</strong> {notification_type || 'Desconhecido'}</div>
            <div><strong>Dados:</strong> {JSON.stringify(meta_data, null, 2)}</div>
          </div>
        );
    }
  };

  return (
    <div style={{ width: '100%', margin: 0, padding: 0 }}>
      <Layout className="main-layout" style={{ minHeight: '100vh', background: '#f5f6fa', width: '100%', margin: 0, padding: 0 }}>
        {/* Floating Hamburger Icon and Drawer */}
        <Header 
          className="main-header"
          style={{ 
            background: '#ffffff', 
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
            width: '100%',
            maxWidth: '100vw',
            minWidth: 0,
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            margin: 0,
          position: 'sticky',
          top: 0,
            zIndex: 1000,
            overflow: 'hidden',
            boxSizing: 'border-box',
            flexWrap: 'nowrap',
          }}
        >
          {/* First Banner - left half of header */}
          <div 
            className="header-banner-container header-banner-first"
            style={{ 
              position: 'absolute', 
              left: 0,
              top: 0, 
              bottom: 0, 
              width: '50%',
              minWidth: 0,
              maxWidth: '50%',
              height: '100%',
              zIndex: 0, 
              pointerEvents: 'none',
              overflow: 'hidden',
            }}
          >
            <VideoBanner 
              videoSrc={`/VideoBanner1.mp4?v=${TOP_BANNER_ASSET_VERSION}`}
              height="100%"
              width="100%"
            />
          </div>
          
          {/* Second Banner - right half of header */}
          <div 
            className="header-banner-container header-banner-second"
            style={{ 
              position: 'absolute', 
              right: 0,
              top: 0, 
              bottom: 0, 
              width: '50%',
              minWidth: 0,
              maxWidth: '50%',
              height: '100%',
              zIndex: 0, 
              pointerEvents: 'none',
              overflow: 'hidden',
            }}
          >
            <VideoBanner 
              videoSrc={`/VideoBanner2.mp4?v=${TOP_BANNER_ASSET_VERSION}`}
              height="100%"
              width="100%"
            />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 2, marginLeft: '-2vw' }}>
            <Button
              type="text"
              onClick={() => setDrawerOpen(true)}
              style={{ 
                background: 'transparent', boxShadow: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', outline: 'none', padding: 0,
                transition: 'background 0.2s',
              }}
              className="crm-hamburger-btn"
              aria-label="Abrir menu"
            >
              <MenuOutlined style={{ fontSize: 'clamp(20px, 2.2vw, 28px)', color: '#fff' }} />
            </Button>
          </div>
          <div className="header-gap" style={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 2, flex: 1, justifyContent: 'flex-end' }}>
            <Button 
              type="text"
              onClick={() => setReportModalVisible(true)}
                className="header-action-btn"
              style={{ 
                background: 'none', 
                border: 'none', 
                display: 'flex', 
                  flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center',
                transition: 'all 0.2s',
                marginTop: '6px'
              }}
              title="Enviar Relatório"
              >
                {USE_SEASONAL_ICONS ? (
                  <img 
                    src={SEASONAL_BUG_ICON_PATH} 
                    alt="Report" 
                    style={{ objectFit: 'contain' }}
                  />
                ) : (
                  <ORIGINAL_BUG_ICON style={{ fontSize: 'clamp(20px, 2.2vw, 28px)', color: '#222' }} />
                )}
                <span style={{ color: '#ffffff', fontWeight: 500, fontSize: 13, lineHeight: 1.1, marginTop: 2, textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 1px 2px rgba(0,0,0,0.75)' }}>Bug</span>
              </Button>
            <Popover
              content={notificationOverlay}
              trigger="click"
              placement="bottomRight"
              open={notifOpen}
              onOpenChange={handleNotificationOpen}
            >
              <Badge count={notifications.filter(n => !n.read).length} size="small" offset={[-2, 2]}>
                <Button 
                  type="text"
                    className="header-action-btn"
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center', 
                      justifyContent: 'center',
                      marginTop: '6px'
                    }}
                  ref={notifBtnRef}
                  >
                    {USE_SEASONAL_ICONS ? (
                      <img 
                        src={SEASONAL_BELL_ICON_PATH} 
                        alt="Notifications" 
                        style={{ objectFit: 'contain' }}
                      />
                    ) : (
                      <ORIGINAL_BELL_ICON style={{ fontSize: 'clamp(20px, 2.2vw, 30px)', color: '#222' }} />
                    )}
                    <span style={{ color: '#ffffff', fontWeight: 500, fontSize: 13, lineHeight: 1.1, marginTop: 2, textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 1px 2px rgba(0,0,0,0.75)' }}>Notificações</span>
                  </Button>
              </Badge>
            </Popover>
            <Dropdown
              menu={{ items: userMenuItems, onClick: ({ key }) => { if (key === 'logout') logout(); else navigate(`/${key}`); } }}
              placement="bottomRight"
            >
              <Button
                type="text"
                  className="header-user-btn"
                style={{ 
                  width: 'auto', 
                  background: 'none', 
                  border: 'none', 
                  display: 'flex', 
                  alignItems: 'center', 
                    justifyContent: 'center',
                  marginTop: '8px'
                }}
              >
                  <div style={{ position: 'relative', marginRight: '0.6vw' }}>
                <Avatar 
                  size={32}
                  src={user?.avatar_url ? (user.avatar_url.startsWith('data:image/') ? user.avatar_url : `${DEPLOYMENT_CONFIG.getBackendUrl()}/${user.avatar_url}`) : undefined}
                      icon={<UserOutlined style={{ fontSize: 'clamp(12px, 1vw, 18px)', color: '#222' }} />}
                    />
                    {USE_SEASONAL_ICONS && (
                      <img 
                        src="/hat.png" 
                        alt="Santa hat" 
                        className="header-hat"
                        style={{ 
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-40%)',
                          objectFit: 'contain',
                          pointerEvents: 'none',
                          zIndex: 10
                        }}
                />
                    )}
                  </div>
                  <span style={{ fontWeight: 600, color: '#ffffff', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 1px 2px rgba(0,0,0,0.75)' }}>{user?.username}</span>
              </Button>
            </Dropdown>
          </div>
        </Header>
        
        <Drawer
          title={<Text strong style={{ fontSize: 22, letterSpacing: 1 }}>Menu</Text>}
          placement="left"
          closable={true}
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={260}
          style={{ borderTopRightRadius: 18, borderBottomRightRadius: 18 }}
          headerStyle={{ borderBottom: '1px solid #f0f0f0', background: '#fff', borderTopRightRadius: 18 }}
          bodyStyle={{ padding: 0, background: '#fff' }}
          className="crm-drawer-menu"
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={getMenuItems()}
            onClick={({ key }) => { handleMenuClick({ key }); setDrawerOpen(false); }}
            style={{ borderRight: 0, height: '100%', fontSize: 18, padding: '16px 0' }}
            theme="light"
            className="crm-menu-list"
          />
          <style>{`
            .crm-hamburger-btn:hover {
              background: transparent !important;
            }
            .crm-drawer-menu .ant-drawer-content {
              background: #fff !important;
              border-top-right-radius: 18px !important;
              border-bottom-right-radius: 18px !important;
              box-shadow: 2px 0 16px 0 rgba(24, 144, 255, 0.08) !important;
              transition: box-shadow 0.3s;
            }
            .crm-drawer-menu .ant-drawer-header {
              background: #fff !important;
              border-top-right-radius: 18px !important;
            }
            .crm-menu-list .ant-menu-item {
              font-size: 18px !important;
              padding: 14px 32px !important;
              border-radius: 8px !important;
              margin: 6px 8px !important;
              transition: background 0.2s, color 0.2s;
            }
            .crm-menu-list .ant-menu-item-selected {
              background: rgba(24, 144, 255, 0.10) !important;
              box-shadow: 0 0 8px 2px rgba(24,144,255,0.13) !important;
              color: #1890ff !important;
            }
            .crm-menu-list .ant-menu-item:hover {
              background: #f0f5ff !important;
              color: #1890ff !important;
            }
            .crm-notification-dropdown-root .crm-notification-dropdown {
              animation: fadeInDown 0.18s cubic-bezier(.4,0,.2,1);
            }
            @keyframes fadeInDown {
              from { opacity: 0; transform: translateY(-16px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </Drawer>
        <Content style={{
          margin: 0,
          minHeight: 'calc(100vh - 86px)',
          maxWidth: '100%',
          width: '100%',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          boxSizing: 'border-box',
          position: 'relative',
        }}>
          {children}
        </Content>

        {/* Notification Details Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {USE_SEASONAL_ICONS ? (
                <img 
                  src={SEASONAL_BELL_ICON_PATH} 
                  alt="Notification" 
                  style={{ width: 28, height: 28, objectFit: 'contain' }}
                />
              ) : (
                <ORIGINAL_BELL_ICON style={{ color: '#1890ff' }} />
              )}
              <span>Detalhes da Notificação</span>
            </div>
          }
          open={notificationModalVisible}
          onCancel={() => {
            setNotificationModalVisible(false);
            setSelectedNotification(null);
          }}
          footer={[
            <Button 
              key="close" 
              onClick={() => {
                setNotificationModalVisible(false);
                setSelectedNotification(null);
              }}
            >
              Fechar
            </Button>
          ]}
          width={500}
        >
          {selectedNotification && (
            <div style={{ padding: '16px 0' }}>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                  {selectedNotification.title}
                </Text>
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <Text>{selectedNotification.message}</Text>
              </div>
              
              {selectedNotification.meta_data && (
                <div style={{ 
                  marginTop: 16, 
                  padding: '12px', 
                  background: '#f5f5f5', 
                  borderRadius: 6,
                  fontSize: 12
                }}>
                  <Text strong>Informações Adicionais:</Text>
                  <div style={{ marginTop: 8 }}>
                    {renderNotificationDetails(selectedNotification)}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Report Modal */}
        <ReportModal
          visible={reportModalVisible}
          onCancel={() => setReportModalVisible(false)}
        />
        
        {/* Chat Modal - Disabled */}
        {/* <ChatModal
          isOpen={chatModalVisible}
          onClose={handleChatModalClose}
          currentUser={user}
        /> */}
      </Layout>
      
      {/* Business Area Pill - positioned absolutely at bottom right, below header */}
      {/* COMMENTED OUT - User requested to remove Energia pill */}
      {/* {canSwitchBusinessAreaFlag && (
        <div style={{
          position: 'fixed',
          right: 32,
          top: 86,
          zIndex: 1001,
          pointerEvents: 'auto',
        }}>
          <Dropdown
            menu={{
              items: businessAreaItems,
              onClick: ({ key }) => handleBusinessAreaChange(key as BusinessArea),
            }}
            placement="bottomRight"
          >
            <Button 
              type="default"
              style={{
                borderRadius: 24,
                padding: '0 22px',
                height: 44,
                fontWeight: 700,
                fontSize: 18,
                background: currentBusinessArea === 'telecommunications' ? '#e6f7ff' : '#fffbe6',
                color: currentBusinessArea === 'telecommunications' ? '#1890ff' : '#faad14',
                border: 'none',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                display: 'flex', alignItems: 'center', gap: 8,
                minWidth: 180,
              }}
              aria-label="Selecionar área de negócio"
            >
              {getBusinessAreaIcon()} {getBusinessAreaLabel()}
            </Button>
          </Dropdown>
        </div>
      )} */}
      
      {/* Floating Chat Button - Disabled */}
      {/* <FloatingChatButton onOpen={handleChatModalOpen} /> */}
    </div>
  );
};

export default MainLayout;
